<!-- eslint-disable vue/component-name-in-template-casing -->
<template>
  <ContentSection
    class="transition-opacity duration-1000 ease-out"
    :class="isReady ? `opacity-100` : `opacity-0`"
  >
    <Container tag="div" class="relative">
      <!-- HEADING -->
      <h2 v-if="data.heading" class="mb-9 font-semibold typo-h3" v-html="data.heading" />

      <!-- DESKTOP SWIPER (3+ facts) -->
      <AnimatedContainer v-if="hasSlider">
        <div class="relative">
          <swiper-container
            ref="sliderRef"
            class="flex"
          >
            <swiper-slide
              v-for="fact in data.facts"
              :key="fact.id"
            >
              <div
                class="relative flex h-full flex-col slide-up"
                :class="{ group: fact.link }"
              >
                <!-- IMAGE -->
                <div
                  v-if="hasImages"
                  class="mb-8 aspect-[4/3] w-full overflow-hidden"
                  :class="{ 'bg-gradient-03': !fact.image }"
                >
                  <AppImage
                    class="
                      size-full object-cover transition-transform duration-500 ease-in-out
                      group-hover-focus:scale-110
                    "
                    :src="fact.image"
                  />
                </div>

                <!-- TEXT -->
                <div
                  class="
                    flex grow flex-col transition duration-100 ease-in
                    group-hover-focus:scale-[1.0025] group-hover-focus:text-secondary-900
                    group-hover-focus:duration-200 group-hover-focus:ease-out
                  "
                >
                  <div
                    :class="{
                      'md:min-h-52': data.facts_text_length?.value === 'short',
                      'md:min-h-[22rem]': data.facts_text_length?.value === 'long',
                    }"
                  >
                    <!-- Title -->
                    <h3 class="mb-2 font-semibold typo-h6">
                      {{ fact.heading }}
                    </h3>

                    <!-- Text -->
                    <div
                      class="mb-4 max-w-text typo-body"
                      v-html="fact.text"
                    />
                  </div>

                  <!-- Link Buttons -->
                  <ul v-if="fact.link_buttons?.length" class="flex flex-wrap gap-x-4">
                    <li v-for="linkButton in fact.link_buttons" :key="linkButton.id">
                      <AppButton
                        v-if="linkButton.link"
                        class="mt-4"
                        :to="linkButton.link"
                        variant="secondary"
                      >
                        {{ linkButton.link_text }}
                      </AppButton>
                    </li>
                  </ul>
                </div>

                <!-- LINK -->
                <NuxtLink
                  v-if="fact.link"
                  :to="fact.link"
                  :aria-label="fact.heading"
                  class="after:absolute after:inset-0"
                />
              </div>
            </swiper-slide>
          </swiper-container>
        </div>
      </AnimatedContainer>

      <!-- MOBILE/FALLBACK GRID (< 3 facts or mobile) -->
      <AnimatedContainer v-else-if="data.facts.length">
        <ul
          class="
            flex flex-col gap-x-default gap-y-default
            md:grid md:grid-cols-[repeat(auto-fill,_minmax(25.75rem,_1fr))]
          "
        >
          <li
            v-for="fact in data.facts"
            :key="fact.id"
            class="relative flex flex-col slide-up"
            :class="{ group: fact.link }"
          >
            <!-- IMAGE -->
            <div
              v-if="hasImages"
              class="mb-8 aspect-[4/3] w-full overflow-hidden"
              :class="{ 'bg-gradient-03': !fact.image }"
            >
              <AppImage
                class="
                  size-full object-cover transition-transform duration-500 ease-in-out
                  group-hover-focus:scale-110
                "
                :src="fact.image"
              />
            </div>

            <!-- TEXT -->
            <div
              class="
                flex grow flex-col transition duration-100 ease-in
                group-hover-focus:scale-[1.0025] group-hover-focus:text-secondary-900
                group-hover-focus:duration-200 group-hover-focus:ease-out
              "
            >
              <div
                :class="{
                  'md:min-h-52': data.facts_text_length?.value === 'short',
                  'md:min-h-[22rem]': data.facts_text_length?.value === 'long',
                }"
              >
                <!-- Title -->
                <h3 class="mb-2 font-semibold typo-h6">
                  {{ fact.heading }}
                </h3>

                <!-- Text -->
                <div
                  class="mb-4 max-w-text typo-body"
                  v-html="fact.text"
                />
              </div>

              <!-- Link Buttons -->
              <ul v-if="fact.link_buttons?.length" class="flex flex-wrap gap-x-4">
                <li v-for="linkButton in fact.link_buttons" :key="linkButton.id">
                  <AppButton
                    v-if="linkButton.link"
                    class="mt-4"
                    :to="linkButton.link"
                    variant="secondary"
                  >
                    {{ linkButton.link_text }}
                  </AppButton>
                </li>
              </ul>
            </div>

            <!-- LINK -->
            <NuxtLink
              v-if="fact.link"
              :to="fact.link"
              :aria-label="fact.heading"
              class="after:absolute after:inset-0"
            />
          </li>
        </ul>
      </AnimatedContainer>

      <!-- NAVIGATION ARROWS (positioned on sides) -->
      <div v-if="hasSlider" class="absolute left-0 top-1/2 z-10 ml-8 -translate-y-1/2 rounded-full bg-white">
        <AppButton
          type="button"
          :disabled="slideFirst"
          variant="secondary"
          @click="swiper?.prev()"
        >
          <template #icon>
            <IconArrowLeft class="size-8" />
          </template>
        </AppButton>
      </div>
      <div v-if="hasSlider" class="absolute right-0 top-1/2 z-10 mr-8 -translate-y-1/2 rounded-full bg-white">
        <AppButton
          type="button"
          :disabled="slideLast"
          variant="secondary"
          @click="swiper?.next()"
        >
          <template #icon>
            <IconArrowRight class="size-8" />
          </template>
        </AppButton>
      </div>
    </Container>
  </ContentSection>
</template>

<script setup lang="ts">
import type { MainComponentsFactsFragment } from '#gql'
import 'swiper/css'

const props = defineProps<{
  data: Enforce<MainComponentsFactsFragment>
}>()

const sliderRef = ref(null)
const isReady = ref(false)
const slideFirst = ref(true)
const slideLast = ref(false)

const hasImages = computed(() => props.data.facts.some(fact => fact?.image))

// Show swiper based on display_type only
const hasSlider = computed(() => {
  return props.data.display_type?.value === 'slider'
})

const swiper = hasSlider.value
  ? useSwiper(sliderRef, {
    loop: false, // Non-infinite as requested
    breakpoints: {
      0: {
        slidesPerView: 1,
        spaceBetween: 16,
      },
      768: {
        slidesPerView: 2,
        spaceBetween: 24,
      },
      1024: {
        slidesPerView: 3,
        spaceBetween: 32,
      },
    },
  })
  : null

onMounted(async () => {
  await nextTick()

  if (hasSlider.value && swiper) {
    const swiperInstance = swiper.instance.value

    if (swiperInstance) {
      swiperInstance.on('reachBeginning', () => {
        slideFirst.value = true
      })
      swiperInstance.on('fromEdge', () => {
        slideFirst.value = false
        slideLast.value = false
      })
      swiperInstance.on('reachEnd', () => {
        slideLast.value = true
      })
    }
  }

  isReady.value = true
})
</script>
